import React, { useState } from 'react';
import config from 'src/config';
import { useAppDispatch, useAppSelector } from 'src/store';
import SnackbarAlert from 'src/shared/components/snackbar-alert/SnackbarAlert';
import TokenDetailModal from './token-detail-modal/TokenDetailModal';
import CreateTokenModal from './create-token-modal/CreateTokenModal';
import RevealTokenModal from './reveal-token-modal/RevealTokenModal';
import RevokeTokenDialog from './revoke-token-dialog/RevokeTokenDialog';
import { getEngineApiTokensModal } from '../state/engineApiTokensSelector';
import { closeModal, openReveal } from '../state/engineApiTokensSlice';
import {
    useCreateEngineApiTokenMutation,
    useRevokeEngineApiTokenMutation,
    useListEngineApiTokenScopesQuery,
    type CreateApiTokenRequest,
} from '../state/api/engineApiTokensEngineSlice';

type SnackbarState = {
    open: boolean;
    severity: 'success' | 'error';
    message: string;
};

const SNACKBAR_AUTO_HIDE_MS = 5000;

const extractBackendError = (err: unknown, fallback: string): string => {
    const candidate = err as { data?: { error?: string } } | undefined;
    return candidate?.data?.error ?? fallback;
};

const EngineApiTokensModals = () => {
    const modal = useAppSelector(getEngineApiTokensModal);
    const dispatch = useAppDispatch();
    const [createToken] = useCreateEngineApiTokenMutation();
    const [revokeToken] = useRevokeEngineApiTokenMutation();
    const { data: scopesData, isLoading: isLoadingScopes } = useListEngineApiTokenScopesQuery();
    const scopes = scopesData?.scopes ?? [];
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, severity: 'success', message: '' });

    const showSnackbar = (severity: SnackbarState['severity'], message: string) => {
        setSnackbar({ open: true, severity, message });
    };

    const closeSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

    const handleClose = () => {
        // The user acknowledged a freshly-revealed token → confirm it was saved.
        if (modal.kind === 'reveal') {
            showSnackbar('success', `Token "${modal.payload.name}" created`);
        }
        dispatch(closeModal());
    };

    const handleCreateSubmit = async (req: CreateApiTokenRequest): Promise<void> => {
        const response = await createToken(req).unwrap();
        dispatch(openReveal(response));
    };

    const handleRevokeConfirm = async (): Promise<void> => {
        if (modal.kind !== 'revoke') return;
        const target = modal.token;
        try {
            await revokeToken(target.id).unwrap();
            showSnackbar('success', `Token "${target.name}" revoked`);
        } catch (err) {
            showSnackbar('error', extractBackendError(err, 'Failed to revoke token.'));
        } finally {
            dispatch(closeModal());
        }
    };

    return (
        <>
            {modal.kind === 'detail' && <TokenDetailModal token={modal.token} onClose={handleClose} />}
            {modal.kind === 'create' && (
                <CreateTokenModal scopes={scopes} isLoadingScopes={isLoadingScopes} onSubmit={handleCreateSubmit} onClose={handleClose} />
            )}
            {modal.kind === 'reveal' && (
                <RevealTokenModal payload={modal.payload} engineApiUrl={config.engineApiUrl} onClose={handleClose} />
            )}
            {modal.kind === 'revoke' && <RevokeTokenDialog token={modal.token} onConfirm={handleRevokeConfirm} onCancel={handleClose} />}
            <SnackbarAlert
                open={snackbar.open}
                severity={snackbar.severity}
                message={snackbar.message}
                autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
                onClose={closeSnackbar}
                dataTestId='engine-api-tokens-snackbar'
            />
        </>
    );
};

export default EngineApiTokensModals;
